<template>
  <div class="bg-white border border-gray-200 rounded-lg overflow-hidden">
    <template v-if="$slots.summary">
      <div class="flex items-start justify-between p-5 border-b border-gray-200">
        <slot name="summary" />
      </div>
    </template>

    <template v-if="isEmpty">
      <div class="bg-gray-100 w-full">
        <slot name="empty">
          <app-empty-state />
        </slot>
      </div>
    </template>

    <template v-else>
      <div class="overflow-x-auto">
        <table :class="['table', $attrs.class]">
          <thead
            class="bg-gray-50"
            data-sentry-unmask
          >
            <tr
              v-for="group in table.getHeaderGroups()"
              :key="group.id"
            >
              <th
                v-for="(header, i) in group.headers"
                :key="header.id"
                class="select-none"
                :style="{ ...header.column.columnDef.meta?.style }"
              >
                <div class="inline-flex items-center gap-1">
                  <template v-if="enableRowSelection && i === 0">
                    <input
                      type="checkbox"
                      :checked="table.getIsAllRowsSelected()"
                      class="checkbox checkbox-sm checkbox-primary mr-2"
                      @click="table.getToggleAllPageRowsSelectedHandler()?.($event)"
                    />
                  </template>
                  <flex-render
                    :render="header.column.columnDef.header"
                    :props="header.getContext()"
                  />
                  <template v-if="header.column.getCanSort()">
                    <data-table-sort-icon
                      :sort-direction="header.column.getIsSorted()"
                      @click="header.column.getToggleSortingHandler()?.($event)"
                    />
                  </template>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            <template v-if="loading">
              <tr
                v-for="i in 10"
                :key="i"
              >
                <td
                  v-for="i in columns.length"
                  :key="i"
                >
                  <div class="min-h-10 flex items-center whitespace-nowrap text-sm text-gray-600">
                    <div class="skeleton h-5 w-full" />
                  </div>
                </td>
              </tr>
            </template>
            <template v-else>
              <tr
                v-for="row in table.getRowModel().rows"
                :key="row.id"
                class="hover:bg-base-200"
                :class="{ 'cursor-pointer': clickableRows, 'is-active-row': isActiveRow(row.id) }"
                @click="handleRowClick(row.original)"
              >
                <td
                  v-for="(cell, i) in row.getVisibleCells()"
                  :key="cell.id"
                  :style="{ ...cell.column.columnDef.meta?.style }"
                  :data-sentry-unmask="cell.column.columnDef.sentryUnmask"
                >
                  <div class="min-h-10 inline-flex items-center whitespace-nowrap text-sm text-gray-600">
                    <template v-if="enableRowSelection && i === 0">
                      <input
                        type="checkbox"
                        :checked="row.getIsSelected()"
                        @click="row.getToggleSelectedHandler()?.($event)"
                        class="checkbox checkbox-sm checkbox-primary mr-3"
                      />
                    </template>
                    <flex-render
                      :render="cell.column.columnDef.cell"
                      :props="cell.getContext()"
                    />
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
      <div
        v-if="pagination.total_pages > 1"
        class="px-5 py-3 border-t border-gray-200"
      >
        <app-pagination
          :loading="loading"
          :page="pagination.page"
          :per_page="pagination.per_page"
          :total_pages="pagination.total_pages"
          @update:page="handlePageChange"
        />
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, watchEffect, computed } from 'vue';
import { useVueTable, getCoreRowModel, getSortedRowModel, FlexRender } from '@tanstack/vue-table';
import DataTableSortIcon from './DataTableSortIcon.vue';

defineOptions({
  inheritAttrs: false,
});

const props = defineProps({
  activeRowId: {
    type: String,
  },
  rowIdKey: {
    type: String,
    default: 'id',
  },
  columns: {
    type: Array,
    required: true,
    default: () => [],
  },
  data: {
    type: Array,
    required: true,
    default: () => [],
  },
  enableRowSelection: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  clickableRows: {
    type: Boolean,
    default: true,
  },
  pagination: {
    type: Object,
    default: () => ({
      page: 1,
      per_page: 20,
      total_pages: 0,
    }),
  },
});

const isEmpty = computed(() => {
  return !props.loading && !props.data.length;
});

const emit = defineEmits(['click:row', 'update:rowSelection', 'update:page']);

const sorting = ref([]);
const rowSelection = ref({});

const isActiveRow = (id) => id === props.activeRowId;

const table = useVueTable({
  get data() {
    return props.data;
  },
  get columns() {
    return props.columns;
  },
  enableRowSelection: props.enableRowSelection,
  state: {
    get sorting() {
      return sorting.value;
    },
    get rowSelection() {
      return rowSelection.value;
    },
  },
  onSortingChange: (updaterOrValue) => {
    sorting.value = typeof updaterOrValue === 'function' ? updaterOrValue(sorting.value) : updaterOrValue;
  },
  onRowSelectionChange: (updaterOrValue) => {
    rowSelection.value = typeof updaterOrValue === 'function' ? updaterOrValue(rowSelection.value) : updaterOrValue;
  },
  getCoreRowModel: getCoreRowModel(),
  getSortedRowModel: getSortedRowModel(),
  getRowId: (originalRow) => originalRow[props.rowIdKey],
});

const handleRowClick = (row) => {
  emit('click:row', row);
};

watchEffect(() => {
  const selectedRows = table.getSelectedRowModel().rows.map((row) => row.original);
  emit('update:rowSelection', selectedRows);
});

const handlePageChange = (page) => {
  emit('update:page', page);
};
</script>

<style>
@reference '@/assets/main.css';

.table {
  @apply bg-white;
  :where(th) {
    @apply px-4 py-3 font-semibold text-xs;
  }

  :where(td) {
    @apply p-4;
  }

  :where(thead tr, tbody tr:not(:last-child)) {
    border-bottom: var(--border) solid var(--color-gray-200);
  }

  :where(tr.is-active-row td) {
    background-color: var(--color-gray-100);
  }
}
</style>
